import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuth } from '@/stores/auth';
import {
  deleteAuthDevices, getAuthDevices, getAuthMe, postAuthLogout, postAuthPasswordChange, postAuthPinReset, postAuthPinResetRequest, putAuthMe,
} from '@/api/auth';
import { getNotificationsPreferences, putNotificationsPreferences } from '@/api/common';
import { getWallet } from '@/api/parent';
import { PinManager } from '../wallet/ui';
import { FingerprintCard } from '@/features/auth/pages/AccountPage';
import { Banner, Empty, ErrorBox, Loading, errMsg, field, fmtDateTime, unwrap, PageTitle, Scroll } from '../ui';

interface Me { user: { first_name: string | null; last_name: string | null; email: string | null; phone: string | null; username: string | null; locale: string | null } }
interface Pref { type: string; label: string; in_app: boolean; push: boolean; sms: boolean; email: boolean }
interface Device { id: number | string; [k: string]: unknown }

type Msg = { tone: 'success' | 'error'; text: string } | null;
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => <label className="block text-sm"><span className="text-slate-500">{label}</span>{children}</label>;

function Profile({ setMsg }: { setMsg: (m: Msg) => void }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['parent-me'], queryFn: () => unwrap<Me>(getAuthMe()) });
  const [form, setForm] = useState<{ first_name: string; last_name: string; email: string; locale: string } | null>(null);
  const u = me.data?.user;
  const f = form ?? { first_name: u?.first_name ?? '', last_name: u?.last_name ?? '', email: u?.email && u.email.includes('@') ? u.email : '', locale: u?.locale ?? 'en' };
  const emailBad = f.email.trim() !== '' && !/^\S+@\S+\.\S+$/.test(f.email.trim());
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { first_name: f.first_name.trim(), last_name: f.last_name.trim(), locale: f.locale };
      const e = f.email.trim();
      if (e !== (u?.email ?? '')) body.email = e.includes('@') ? e : null; // only send email when it was really changed
      return putAuthMe(body);
    },
    onSuccess: () => { setMsg({ tone: 'success', text: 'Profile updated.' }); setForm(null); qc.invalidateQueries({ queryKey: ['parent-me'] }); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  return (
    <Card title="Profile">
      {me.isLoading ? <Loading /> : me.isError ? <ErrorBox error={me.error} onRetry={() => me.refetch()} /> : (
        <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
          <Row label="Phone (you sign in with this)"><input className={`${field} bg-slate-50`} value={u?.phone ?? '—'} readOnly /></Row>
          <div className="hidden sm:block" />
          <Row label="First name"><input className={field} autoComplete="given-name" value={f.first_name} onChange={(e) => setForm({ ...f, first_name: e.target.value })} /></Row>
          <Row label="Last name"><input className={field} autoComplete="family-name" value={f.last_name} onChange={(e) => setForm({ ...f, last_name: e.target.value })} /></Row>
          <Row label="Email (optional)"><input className={field} type="email" autoComplete="off" name="contact-email" placeholder="you@example.com" value={f.email} onChange={(e) => setForm({ ...f, email: e.target.value })} />{emailBad && <span className="text-xs text-red-600">Enter a real email address, or leave it empty.</span>}</Row>
          <Row label="Language">
            <select className={field} value={f.locale} onChange={(e) => setForm({ ...f, locale: e.target.value })}>
              <option value="en">English</option><option value="ha">Hausa</option><option value="yo">Yoruba</option><option value="ig">Igbo</option>
            </select>
          </Row>
          <div className="sm:col-span-2"><Button disabled={!form || save.isPending || emailBad || !f.first_name.trim()} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save changes'}</Button></div>
        </div>
      )}
    </Card>
  );
}

function Password({ setMsg }: { setMsg: (m: Msg) => void }) {
  const [v, setV] = useState({ current_password: '', password: '', password_confirmation: '' });
  const save = useMutation({
    mutationFn: () => postAuthPasswordChange(v),
    onSuccess: () => { setMsg({ tone: 'success', text: 'Password changed. Other devices were signed out.' }); setV({ current_password: '', password: '', password_confirmation: '' }); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  return (
    <Card title="Change password">
      <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
        <input type="password" autoComplete="current-password" className={field} placeholder="Current password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} />
        <input type="password" autoComplete="new-password" className={field} placeholder="New password (min 8)" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} />
        <input type="password" autoComplete="new-password" className={field} placeholder="Confirm new password" value={v.password_confirmation} onChange={(e) => setV({ ...v, password_confirmation: e.target.value })} />
      </div>
      <div className="mt-3"><Button disabled={save.isPending || !v.current_password || v.password.length < 8 || v.password !== v.password_confirmation} onClick={() => save.mutate()}>Change password</Button></div>
    </Card>
  );
}

function Pin({ setMsg }: { setMsg: (m: Msg) => void }) {
  const qc = useQueryClient();
  const wallet = useQuery({ queryKey: ['parent-wallet'], queryFn: () => getWallet() });
  const [open, setOpen] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [pin, setPin] = useState('');
  const [conf, setConf] = useState('');
  const [code, setCode] = useState('');
  const fail = (e: unknown) => setMsg({ tone: 'error', text: errMsg(e) });
  const reqReset = useMutation({ mutationFn: () => postAuthPinResetRequest(), onSuccess: () => setMsg({ tone: 'success', text: 'A 6-digit reset code has been sent to your phone.' }), onError: fail });
  const reset = useMutation({ mutationFn: () => postAuthPinReset({ code, new_pin: pin, new_pin_confirmation: conf }), onSuccess: () => { setMsg({ tone: 'success', text: 'PIN reset.' }); setForgot(false); setPin(''); setConf(''); setCode(''); qc.invalidateQueries({ queryKey: ['parent-wallet'] }); }, onError: fail });
  const hasPin = !!wallet.data?.has_pin;
  const box = (ph: string, val: string, on: (v: string) => void, len = 4) => <input type="password" inputMode="numeric" className={`${field} text-center tracking-[0.4em]`} placeholder={ph} maxLength={len} value={val} onChange={(e) => on(e.target.value.replace(/\D/g, '').slice(0, len))} />;

  return (
    <Card title="Transaction PIN">
      {wallet.isLoading ? <Loading /> : (
        <>
          <p className="text-sm text-slate-500 mb-3">{hasPin ? 'Your 4-digit PIN is set. You enter it to pay fees and send money.' : 'Set a 4-digit PIN to pay fees and send money from your wallet.'}</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setOpen(true)}>{hasPin ? 'Change PIN' : 'Set PIN'}</Button>
            {hasPin && <Button variant="ghost" onClick={() => setForgot((v) => !v)}>Forgot PIN?</Button>}
          </div>
          {forgot && (
            <div className="mt-4 rounded-xl border border-slate-200 p-3 space-y-3 max-w-md">
              <p className="text-sm text-slate-600">We'll send a code to your phone. Enter it with your new PIN.</p>
              <Button variant="ghost" disabled={reqReset.isPending} onClick={() => reqReset.mutate()}>{reqReset.isPending ? 'Sending…' : 'Send reset code'}</Button>
              <div className="grid grid-cols-3 gap-2">{box('Code', code, setCode, 6)}{box('New PIN', pin, setPin)}{box('Confirm', conf, setConf)}</div>
              <Button disabled={code.length !== 6 || pin.length !== 4 || pin !== conf || reset.isPending} onClick={() => reset.mutate()}>Reset PIN</Button>
            </div>
          )}
        </>
      )}
      {open && <PinManager hasPin={hasPin} onClose={() => setOpen(false)} onDone={(m) => { setOpen(false); setMsg({ tone: 'success', text: m }); qc.invalidateQueries({ queryKey: ['parent-wallet'] }); }} />}
    </Card>
  );
}

function Preferences({ setMsg }: { setMsg: (m: Msg) => void }) {
  const q = useQuery({ queryKey: ['parent-notif-prefs'], queryFn: () => unwrap<Pref[]>(getNotificationsPreferences()) });
  const [edit, setEdit] = useState<Pref[] | null>(null);
  const rows = edit ?? q.data ?? [];
  const save = useMutation({
    mutationFn: () => putNotificationsPreferences({ preferences: rows.map(({ type, in_app, push, sms, email }) => ({ type, in_app, push, sms, email })) }),
    onSuccess: () => { setMsg({ tone: 'success', text: 'Notification preferences saved.' }); setEdit(null); q.refetch(); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  const flip = (type: string, k: 'in_app' | 'push' | 'sms' | 'email') => setEdit(rows.map((r) => (r.type === type ? { ...r, [k]: !r[k] } : r)));
  return (
    <Card title="Notification preferences">
      {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !rows.length ? <Empty text="No preferences available." /> : (
        <>
          <Scroll><table className="w-full text-sm min-w-[520px]">
            <thead><tr className="text-left text-slate-500"><th className="py-1">Type</th><th>In-app</th><th>Push</th><th>SMS</th><th>Email</th></tr></thead>
            <tbody>
              {rows.map((r) => <tr key={r.type} className="border-t"><td className="py-1.5">{r.label}</td>{(['in_app', 'push', 'sms', 'email'] as const).map((k) => <td key={k}><input type="checkbox" checked={r[k]} onChange={() => flip(r.type, k)} /></td>)}</tr>)}
            </tbody>
          </table></Scroll>
          <div className="mt-3"><Button disabled={!edit || save.isPending} onClick={() => save.mutate()}>Save preferences</Button></div>
        </>
      )}
    </Card>
  );
}

function Devices({ setMsg }: { setMsg: (m: Msg) => void }) {
  const q = useQuery({ queryKey: ['parent-devices'], queryFn: () => unwrap<Device[]>(getAuthDevices()) });
  const del = useMutation({
    mutationFn: (id: number | string) => deleteAuthDevices(id),
    onSuccess: () => { setMsg({ tone: 'success', text: 'Device removed.' }); q.refetch(); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });
  const name = (d: Device) => String(d.name ?? d.device_name ?? d.device_uuid ?? d.platform ?? `Device ${d.id}`);
  const seen = (d: Device) => { const t = d.last_used_at ?? d.last_seen_at ?? d.last_active_at; return typeof t === 'string' ? fmtDateTime(t) : ''; };
  return (
    <Card title="Signed-in devices">
      {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !q.data?.length ? <Empty text="No registered devices." /> : (
        <ul className="divide-y text-sm">
          {q.data.map((d) => <li key={d.id} className="py-2 flex justify-between items-center"><span>{name(d)} <span className="text-slate-500">{seen(d)}</span></span><Button variant="ghost" disabled={del.isPending} onClick={() => del.mutate(d.id)}>Remove</Button></li>)}
        </ul>
      )}
    </Card>
  );
}

export function SettingsPage() {
  const [msg, setMsg] = useState<Msg>(null);
  const logout = useAuth((s) => s.logout);
  const signOut = async () => { try { await postAuthLogout(); } finally { logout(); } };
  return (
    <div className="space-y-5">
      <PageTitle kicker="Me" title="Account & security" />
      {msg && <Banner tone={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Banner>}
      <Profile setMsg={setMsg} />
      <Password setMsg={setMsg} />
      <Pin setMsg={setMsg} />
      <FingerprintCard setMsg={setMsg} name={useAuth.getState().user?.name ?? ''} role="parent" />
      <Preferences setMsg={setMsg} />
      <Devices setMsg={setMsg} />
      <Card title="Session"><Button variant="danger" onClick={signOut}>Sign out</Button></Card>
    </div>
  );
}
